import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { getLabels, formatTimestampFromDB } from './DashboardComponent';

const color = '#e74c3c';
const submenuOptions = ['1s', '5s', '1m', '5m'];

function MicrophoneChart({ data, last, interval: propInterval, onIntervalChange }) {
  const [interval, setIntervalValue] = useState(propInterval || '1s');
  const chartRef = useRef();
  const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const chartData = React.useMemo(() => ({
    labels: getLabels(sortedData, interval),
    datasets: [
      {
        label: 'Microphone (dB)',
        data: sortedData.map(d => d.microphone_db),
        borderColor: color,
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? color : 'rgba(231,76,60,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
        borderWidth: 2,
        yAxisID: 'y',
      },
      {
        label: 'Frequency (Hz)',
        data: sortedData.map(d => d.frequency),
        borderColor: '#3498db',
        backgroundColor: 'rgba(52, 152, 219, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#3498db' : 'rgba(52,152,219,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 1.5,
        borderDash: [5, 5],
        yAxisID: 'y1',
      }
    ],
  }), [sortedData, interval]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chartInstance) {
      const chart = chartRef.current.chartInstance;
      chart.data.labels = chartData.labels;
      chart.data.datasets = chartData.datasets;
      chart.update('none');
    }
  }, [chartData]);

  // Sincronizează intervalul cu prop-ul
  useEffect(() => {
    if (propInterval && propInterval !== interval) {
      setIntervalValue(propInterval);
    }
  }, [propInterval, interval]);

  // LIVE dacă ultima valoare e în ultimele 10 minute
  const isLive = last && (Date.now() - new Date(last.created_at).getTime() < 10 * 60 * 1000);
  const lastDbValue = last ? `${last.microphone_db} dB` : '-';
  const lastFreqValue = last ? `${last.frequency} Hz` : '-';
  const lastTime = last ? formatTimestampFromDB(last.created_at) : '-';

  // Calculează axa Y dinamică pentru decibeli
  const dbValues = sortedData.map(d => d.microphone_db).filter(v => typeof v === 'number');
  let minDb = 0, maxDb = 100;
  if (dbValues.length) {
    minDb = Math.floor(Math.min(...dbValues) - 0.1 * Math.abs(Math.min(...dbValues)));
    maxDb = Math.ceil(Math.max(...dbValues) + 0.1 * Math.abs(Math.max(...dbValues)));
    if (minDb === maxDb) { minDb -= 5; maxDb += 5; }
  }

  // Calculează axa Y dinamică pentru frecvență
  const freqValues = sortedData.map(d => d.frequency).filter(v => typeof v === 'number');
  let minFreq = 0, maxFreq = 1000;
  if (freqValues.length) {
    minFreq = Math.floor(Math.min(...freqValues) - 0.1 * Math.abs(Math.min(...freqValues)));
    maxFreq = Math.ceil(Math.max(...freqValues) + 0.1 * Math.abs(Math.max(...freqValues)));
    if (minFreq === maxFreq) { minFreq -= 50; maxFreq += 50; }
  }

  const handleIntervalChange = async (opt) => {
    setIntervalValue(opt);
    if (onIntervalChange) {
      onIntervalChange('microphone', opt);
    }
  };

  // Funcție pentru export CSV
  function exportCsv(data, imei) {
    const now = Date.now();
    const fifteenMinutesAgo = now - 15 * 60 * 1000;
    const filtered = data.filter(d =>
      d.imei === imei && new Date(d.created_at).getTime() >= fifteenMinutesAgo
    );
    const header = 'timestamp,imei,microphone_db,frequency_hz\n';
    const rows = filtered.map(d => [
      d.created_at,
      d.imei,
      d.microphone_db ?? '',
      d.frequency ?? ''
    ].join(','));
    const csv = header + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `microphone_${imei}_${new Date().toISOString().slice(0,16).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ marginBottom: 0, height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: 12, marginBottom: 2, color, textTransform: 'uppercase', letterSpacing: 1, gap: 7, flexWrap: 'wrap' }}>
        <span style={{fontSize: 12}}>MICROPHONE</span>
        {isLive && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#27ae60', animation: 'pulse 1s infinite' }} />
            <span style={{ fontWeight: 600, color: '#27ae60', fontSize: 11 }}>LIVE</span>
          </span>
        )}
        {submenuOptions.map(opt => (
          <button
            key={opt}
            onClick={() => handleIntervalChange(opt)}
            style={{
              padding: '1px 7px',
              borderRadius: 14,
              border: interval === opt ? '2px solid #e74c3c' : '1px solid #bbb',
              background: interval === opt ? '#ffeaea' : '#fff',
              color: interval === opt ? '#e74c3c' : '#222',
              fontWeight: interval === opt ? 'bold' : 'normal',
              fontSize: 10,
              cursor: 'pointer',
              boxShadow: interval === opt ? '0 0 0 2px #e74c3c55' : '0 1px 2px #eee',
              transition: 'all 0.2s',
              outline: interval === opt ? 'none' : undefined,
              marginLeft: 3
            }}
            tabIndex={-1}
            title="Perioada de colectare"
          >
            {opt}
          </button>
        ))}
        {/* Buton export CSV */}
        <button
          style={{
            marginLeft: 8,
            padding: '2px 8px',
            fontSize: 11,
            borderRadius: 6,
            border: '1px solid #e74c3c',
            background: '#f5faff',
            color: '#e74c3c',
            cursor: 'pointer',
            fontWeight: 500
          }}
          title="Exportă CSV microfon"
          onClick={() => exportCsv(data, last?.imei)}
        >
          Export CSV
        </button>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6, fontWeight: 400 }}>{lastTime}</span>
      </div>
      
      {/* Current values for microphone */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 6, 
        marginBottom: 12, 
        padding: '10px 14px', 
        background: 'rgba(255,255,255,0.9)', 
        borderRadius: 10, 
        border: '1px solid #ddd',
        fontSize: 12,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: color, minWidth: 70 }}>Microphone:</span>
          <span style={{ color: color, fontWeight: 500 }}>Decibels: {lastDbValue}</span>
          <span style={{ color: '#3498db', fontWeight: 500 }}>Frequency: {lastFreqValue}</span>
        </div>
      </div>
      <div style={{ height: 280, marginTop: 8 }}>
        <Line ref={chartRef} data={chartData} options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                },
                boxWidth: 20,
                boxHeight: 8
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label + ': ' + context.parsed.y.toFixed(2);
                  if (context.dataset.label.includes('dB')) {
                    label += ' dB';
                  } else if (context.dataset.label.includes('Hz')) {
                    label += ' Hz';
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              min: minDb,
              max: maxDb,
              title: {
                display: true,
                text: 'Decibels (dB)',
                font: { size: 13, weight: 'bold' }
              },
              ticks: {
                stepSize: Math.ceil((maxDb-minDb)/5),
                font: { size: 12 },
                padding: 10
              },
              grid: {
                color: '#eee',
                lineWidth: 1.5,
                drawTicks: true
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              min: minFreq,
              max: maxFreq,
              title: {
                display: true,
                text: 'Frequency (Hz)',
                font: { size: 13, weight: 'bold' }
              },
              ticks: {
                stepSize: Math.ceil((maxFreq-minFreq)/5),
                font: { size: 12 },
                padding: 10
              },
              grid: {
                drawOnChartArea: false,
              }
            },
            x: {
              grid: {
                color: '#f5f5f5',
                lineWidth: 1
              },
              ticks: {
                font: { size: 12 },
                padding: 8,
                maxTicksLimit: 8
              }
            }
          }
        }} />
      </div>
    </div>
  );
}

export default MicrophoneChart; 